import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Container, Paper, Stack, Typography } from '@mui/material';
import { Breadcrumbs } from '../../../components/shared';
import useStyles from './style';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import BREADCRUMB_NAMES from '../../../constants/breadcrumbNames';
import { routeConstants } from '../../../constants/routeConstants';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { rentDetailsFormAction } from '../../../redux-slice/lease';
import { useDispatch, useSelector } from 'react-redux';
import ConfirmationModal from '../../../components/ConfirmationModel/ConfirmationModal';
import {
  useGetLeaseByIdQuery,
  useGetLeaseByIdWithVersionQuery,
  useUpdateLeaseByIdMutation,
} from '../../../services/Lease/LeaseService';
import dayjs from 'dayjs';
import RentDetailsForm from '../../../components/RentDetailsForm';
import { parseConflictMessage } from '../../ManageIndepenantRep/parseConflictMessage';
import { skipToken } from '@reduxjs/toolkit/query';
import { LEASE_COIN_COST_UNIT_OF_MEASURES } from '../../../constants/rentDetailsConfig';
const EditLease = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const classes = useStyles();
  const params = useParams();
  const leaseId = Number(params.id);
  const selectedVersion = location.state?.version;
  const historyActionType = location.state?.type;
  const dispatch = useDispatch();
  const {
    LEASE: { LEASE, LEASE_DETAILS, EDIT_LEASE },
  } = BREADCRUMB_NAMES;
  const breadcrumbData = useMemo(
    () => [
      { text: LEASE, redirection: `${routeConstants.LEASE_ROUTE}` },
      {
        text: LEASE_DETAILS,
        redirection: `${routeConstants.LEASE_ROUTE}/${leaseId}`,
      },
      { text: EDIT_LEASE },
    ],
    [],
  );
  const [modal, setModal] = useState(!historyActionType);
  const [conflictError, setConflictError] = useState(false);
  const [lastAttemptedValues, setLastAttemptedValues] = useState(null);
  const [errorModal, setErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const {
    currentData: latestLeaseDetail,
    isLoading,
    isFetching,
  } = useGetLeaseByIdQuery(leaseId, {
    skip: Boolean(selectedVersion),
  });
  const {
    data: versionedLeaseDetail,
    isLoading: isVersionLoading,
    isFetching: isVersionFetching,
  } = useGetLeaseByIdWithVersionQuery(selectedVersion ? { leaseId, version: selectedVersion } : skipToken);
  const leaseDetail = selectedVersion ? versionedLeaseDetail : latestLeaseDetail;

  const [updateLease] = useUpdateLeaseByIdMutation();
  const updatedById = useSelector((state) => state.user?.userDetails?.id);

  const onUpdateClickHandler = (values) => {
    setLastAttemptedValues(values);
    updateLease({
      id: Number(leaseId),
      updatedBy: updatedById,
      ...(selectedVersion ? { version: selectedVersion } : {}),
      ...values,
    })
      .then((res) => {
        if (res.error) {
          if (res.error.status === 409) {
            // Handle 409 conflict error
            setConflictError(true);
            const errorMsg = res.error.data?.message || 'A conflict occurred while updating the lease.';
            setErrorMessage(errorMsg);
            setErrorModal(true);
          } else {
            // Handle other error types
            setConflictError(false);
            setErrorMessage(res.error.data?.message);
            setErrorModal(true);
          }
          return;
        }
        if (res.data) {
          navigate(`/${routeConstants.LEASE_ROUTE}/${leaseId}`, {
            replace: true,
          });
        }
      })
      .catch((error) => {
        let errorMsg;
        // Try to extract a more specific error message if available
        if (error.status === 409) {
          setConflictError(true);
          errorMsg = error.data?.message || 'A conflict occurred while updating the lease.';
        } else {
          setConflictError(false);
          errorMsg = error.data?.message || error.message || 'An error occurred while updating the lease.';
        }

        setErrorMessage(errorMsg);
        setErrorModal(true);
      });
  };

  const onOverrideClickHandler = (value) => {
    if (value === 'confirm') {
      updateLease({
        id: Number(leaseId),
        updatedBy: updatedById,
        ...(selectedVersion ? { version: selectedVersion } : {}),
        ...lastAttemptedValues,
        override: true,
      })
        .then((res) => {
          if (res.error) {
            setErrorMessage(res.error.data?.message || 'Failed to override and update lease.');
          } else if (res.data) {
            setErrorModal(false);
            setConflictError(false);
            navigate(`/${routeConstants.LEASE_ROUTE}/${leaseId}`, {
              replace: true,
            });
          }
        })
        .catch((error) => {
          const errorMsg = error.data?.message || error.message || 'Failed to override and update lease.';
          setErrorMessage(errorMsg);
        });
    } else {
      dispatch(rentDetailsFormAction(lastAttemptedValues));
      setErrorModal(false);
      setErrorMessage('');
      setConflictError(false);
    }
  };

  const onCancelClickHandler = () => {
    navigate(`/${routeConstants.LEASE_ROUTE}/${leaseId}`, { replace: true });
  };

  useEffect(() => {
    if (!leaseDetail) return;

    const subLocations =
      leaseDetail?.contractSublocations
        ?.map((value) => value?.sublocation)
        ?.filter(
          (subLocation, index, arr) =>
            subLocation?.id && arr.findIndex((item) => item?.id === subLocation.id) === index,
        ) || [];
    const mappedModalTypes =
      leaseDetail?.contractAssetGameCategoryRates?.map((rate) => ({
        id: rate?.gameCategory?.id,
        name: rate?.gameCategory?.name || '',
        rate: rate?.rentRate ?? 0,
      })) || [];
    const addressParts = [
      leaseDetail?.location?.address1,
      leaseDetail?.location?.city?.name,
      leaseDetail?.location?.state?.name,
      leaseDetail?.location?.postcode,
      leaseDetail?.location?.country?.name,
    ].filter(Boolean); // Remove any undefined/null values

    const fullAddress = addressParts.join(', ') || '';
    dispatch(
      rentDetailsFormAction({
        leaseName: leaseDetail?.lease_name || '',
        location: leaseDetail?.location
          ? {
              id: leaseDetail.location.id,
              name: leaseDetail.location.locationName,
              address1: leaseDetail.location.address1,
              city: leaseDetail.location.city?.name,
              state: leaseDetail.location.state?.name,
              zipCode: leaseDetail.location.postcode,
              country: leaseDetail.location.country?.name,
            }
          : null,
        subLocation: subLocations,
        sublocationsOfLocation: [], // No data source in leaseDetail; fallback to empty
        city: leaseDetail?.location?.city?.id || '',
        state: leaseDetail?.location?.state?.id || '',
        zipCode: leaseDetail?.location?.postcode || '',
        address1: fullAddress || '',
        address2: leaseDetail?.location?.address2 || '',
        country: leaseDetail?.location?.country?.id || '',
        boothInfo: leaseDetail?.boothInfo || '',
        dba: leaseDetail?.dba || '',
        leaseType: leaseDetail?.leaseType || '',
        status: leaseDetail?.status || '',
        leaseBegins: leaseDetail?.termStartDate ? dayjs(leaseDetail.termStartDate).toString() : null,
        leaseEnds: leaseDetail?.termEndDate ? dayjs(leaseDetail.termEndDate).toString() : null,
        rentType: leaseDetail?.rentType,
        rentRate: leaseDetail?.rate || 0.0,
        calculationStarts: leaseDetail?.calculationStarts ? dayjs(leaseDetail.calculationStarts).toString() : null,
        calculationEnds: leaseDetail?.calculationEnds ? dayjs(leaseDetail.calculationEnds).toString() : null,
        breakpointNatural: leaseDetail?.breakpointNatural ?? true,
        breakpointValue: leaseDetail?.breakpointValue || 0.0,
        breakpointRevenue: leaseDetail?.breakpointRevenue || '',
        resetTerm: leaseDetail?.resetTerm || '',
        payFrequency: leaseDetail?.payFrequency || '',
        CAMFee: leaseDetail?.CAMFee || 0.0,
        rentFee: leaseDetail?.fee || '',
        CAMRate: leaseDetail?.CAMRate || '',
        thresholdRevenue: leaseDetail?.thresholdRevenue || 0.0,
        breakpointRate: leaseDetail?.breakpointRate || 0.0,
        notes: leaseDetail?.notes || '',
        contractLink: leaseDetail?.contractLink || '',
        inEffectFrom: leaseDetail?.in_effect_from ? dayjs(leaseDetail.in_effect_from).format('YYYY-MM-DD') : null,
        inEffectTo: leaseDetail?.in_effect_to ? dayjs(leaseDetail.in_effect_to).format('YYYY-MM-DD') : null,
        tierSummaryState: leaseDetail?.tierSummary?.length
          ? leaseDetail.tierSummary
          : [
              { startBreakpoint: 0.01, tierThreshold: '', tierRate: '' },
              { startBreakpoint: '', tierThreshold: '', tierRate: '' },
            ],
        coinCost: leaseDetail?.coinCost || '',
        coinCostUom: leaseDetail?.coinCostUom || LEASE_COIN_COST_UNIT_OF_MEASURES.DOLLAR,
        creditCardFeeRate: leaseDetail?.creditCardFeeRate ?? '',
        cardCost: leaseDetail?.cardCost || '',
        modalTypes: mappedModalTypes,
        rentOptions: leaseDetail?.contractRentOptions?.map((cro) => cro.rentOption) || [],
      }),
    );
  }, [leaseDetail, dispatch, historyActionType]);

  useEffect(() => {
    if (historyActionType !== 'add') return;
    if (!leaseDetail) return;

    const mappedModalTypes =
      leaseDetail?.contractAssetGameCategoryRates?.map((rate) => ({
        id: rate?.gameCategory?.id,
        name: rate?.gameCategory?.name || '',
        rate: rate?.rentRate ?? 0,
      })) || [];

    dispatch(
      rentDetailsFormAction({
        leaseName: leaseDetail?.lease_name || '',
        inEffectFrom: null,
        inEffectTo: null,
        modalTypes: mappedModalTypes,
        rentOptions: leaseDetail?.contractRentOptions?.map((cro) => cro.rentOption) || [],
        coinCostUom: leaseDetail?.coinCostUom || LEASE_COIN_COST_UNIT_OF_MEASURES.DOLLAR,
        creditCardFeeRate: leaseDetail?.creditCardFeeRate ?? '',
        tierSummaryState: leaseDetail?.tierSummary?.length
          ? leaseDetail.tierSummary
          : [
              { startBreakpoint: 0.01, tierThreshold: '', tierRate: '' },
              { startBreakpoint: '', tierThreshold: '', tierRate: '' },
            ],
      }),
    );
  }, [
    historyActionType,
    dispatch,
    leaseDetail?.lease_name,
    leaseDetail?.tierSummary,
    leaseDetail?.contractAssetGameCategoryRates,
  ]);

  const onConfirmationClickHandler = (value) => {
    switch (value) {
      case 'EDIT':
        setModal(false);
        break;
      case 'CANCEL':
        navigate(`/${routeConstants.LEASE_ROUTE}/${leaseId}`, {
          replace: true,
        });
        break;
      case 'CREATE_NEW':
        dispatch(
          rentDetailsFormAction({
            subLocation: [],
            location: null,
            leaseName: '',
            leaseBegins: null,
            leaseEnds: null,
            inEffectFrom: null,
            inEffectTo: null,
            rentType: '',
            rentFee: '',
            rentRate: '',
            CAMFee: '',
            CAMRate: '',
            breakpointRevenue: '',
            breakpointRate: '',
            resetTerm: '',
            payFrequency: '',
            coinCost: '',
            cardCost: '',
            modalTypes: [],
            rentOptions: [],
            coinCostUom: LEASE_COIN_COST_UNIT_OF_MEASURES.DOLLAR,
            creditCardFeeRate: '',
            tierSummaryState: [
              { startBreakpoint: '0.01', tierThreshold: '', tierRate: '' },
              { startBreakpoint: '', tierThreshold: '', tierRate: '' },
            ],
            contractLink: '',
            notes: '',
            productTypes: [],
            address1: '',
            address2: '',
            zipCode: '',
            country: '',
            state: '',
            city: '',
            dba: '',
            boothInfo: '',
            calculationStarts: null,
            calculationEnds: null,
            breakpointNatural: true,
            breakpointValue: 0.0,
            thresholdRevenue: 0.0,
            leaseType: '',
            status: '',
            sublocationsOfLocation: [],
          }),
        );
        navigate(`/${routeConstants.ADD_LEASE_ROUTE}`, { replace: true });
        break;
    }
  };

  if (isLoading || isFetching || isVersionLoading || isVersionFetching) {
    return <LoadingSpinner containerHeight={400} />;
  }

  return (
    <Container maxWidth="xl" disableGutters className={classes.root}>
      <Box>
        <Breadcrumbs variant="body2" underline="hover" data={breadcrumbData} />
      </Box>

      <Box mb={3}>
        <Stack direction={'row'} alignItems="center" spacing={1.5}>
          <Typography variant="h1" data-testid="leaseName">
            {leaseDetail?.lease_name}
          </Typography>
          <Chip
            label={leaseDetail?.leaseStatus}
            color={
              leaseDetail?.leaseStatus === 'PENDING'
                ? 'primary'
                : leaseDetail?.leaseStatus === 'ACTIVE'
                ? 'success'
                : 'error'
            }
            variant="outlined"
            data-testid="status"
          />
        </Stack>
      </Box>
      <Box component={Paper} pt={3} pl={3} pb={4}>
        <Box mt={1} mx={4}>
          <RentDetailsForm
            isUpdate
            onUpdateClick={onUpdateClickHandler}
            onCancelClick={onCancelClickHandler}
            isLoading={isLoading}
            maxInEffectFromDate={leaseDetail?.maxInEffectFrom}
            historyActionType={historyActionType}
          />
        </Box>
      </Box>
      <ConfirmationModal
        isOpen={modal}
        title="Confirmation"
        msg="Are you sure you want to edit the Lease & Rent details? These changes will be applied on all assigned Sub locations."
        buttons={[
          { text: 'Edit', value: 'EDIT' },
          { text: 'Cancel', value: 'CANCEL' },
          { text: 'Create New', value: 'CREATE_NEW' },
        ]}
        onClick={onConfirmationClickHandler}
      />

      {/* Error Modal for 409 Conflict */}
      <ConfirmationModal
        isOpen={errorModal}
        title={
          <Stack direction="row" spacing={1}>
            <Box>
              <Typography variant="h4" color="black">
                {conflictError ? 'Update Confirmation' : 'Error'}
              </Typography>
            </Box>
          </Stack>
        }
        msg={conflictError ? parseConflictMessage(errorMessage) : errorMessage}
        buttons={
          conflictError
            ? [
                { text: 'Confirm', value: 'confirm' },
                { text: 'Cancel', value: 'cancel' },
              ]
            : [{ text: 'Close', value: 'close' }]
        }
        onClick={conflictError ? onOverrideClickHandler : () => setErrorModal(false)}
      />
    </Container>
  );
};

export default EditLease;
